import { useEffect, useState } from "react";
import api, { openPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { AlertTriangle, FileDown } from "lucide-react";

/**
 * Runde 126.7 – Ausschuss-Konto
 * ------------------------------------------------------------------
 * Liste aller Chargen mit Ausschuss-Tüten. Zeigt Datum, Artikel,
 * Chargennr., Anzahl, Prozentsatz an Ist-Menge. Erlaubt Aufruf des
 * Abschluss-PDFs pro Zeile.
 */
export default function Ausschuss() {
  const [data, setData] = useState({ entries: [], total_waste: 0, total_actual: 0, overall_pct: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/production/ausschuss")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="ausschuss-page">
      <PageHeader
        title="Ausschuss-Konto"
        description="Alle Chargen mit defekten Tüten – nach Datum sortiert. Prozentsatz = Ausschuss ÷ Ist-Menge × 100."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard label="Ausschuss gesamt" value={`${data.total_waste.toLocaleString("de-DE")} Tüten`} accent="text-red-600 dark:text-red-400" />
        <StatCard label="Produziert gesamt" value={`${data.total_actual.toLocaleString("de-DE")} Tüten`} accent="text-[var(--text)]" />
        <StatCard label="Quote gesamt" value={`${data.overall_pct.toFixed(2)} %`} accent={data.overall_pct > 5 ? "text-red-600 dark:text-red-400" : data.overall_pct > 2 ? "text-orange-600 dark:text-orange-400" : "text-emerald-600 dark:text-emerald-400"} />
      </div>

      <div className="rounded-2xl border border-[var(--border)] overflow-hidden bg-[var(--surface)]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="overline">Datum</TableHead>
              <TableHead className="overline">Artikel</TableHead>
              <TableHead className="overline">Charge</TableHead>
              <TableHead className="overline text-right">Ist-Menge</TableHead>
              <TableHead className="overline text-right">Ausschuss</TableHead>
              <TableHead className="overline text-right">Quote</TableHead>
              <TableHead className="overline text-right">Protokoll</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-[var(--text-muted)]">Lade …</TableCell></TableRow>
            )}
            {!loading && data.entries.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-[var(--text-muted)]">Kein Ausschuss erfasst – gute Arbeit! 🎉</TableCell></TableRow>
            )}
            {data.entries.map((e) => (
              <TableRow key={e.id} data-testid={`ausschuss-row-${e.charge_number}`}>
                <TableCell className="font-mono text-xs">{e.date || "—"}</TableCell>
                <TableCell className="text-sm">
                  <div className="font-semibold">{e.item_name}</div>
                  <div className="text-[10px] text-[var(--text-muted)] font-mono">{e.item_sku}</div>
                </TableCell>
                <TableCell className="font-mono text-xs">{e.charge_number}</TableCell>
                <TableCell className="text-right font-mono">{Number(e.actual_quantity).toLocaleString("de-DE")}</TableCell>
                <TableCell className="text-right font-mono text-red-600 dark:text-red-400 font-bold">{Number(e.waste_bags).toLocaleString("de-DE")}</TableCell>
                <TableCell className="text-right font-mono">
                  <span className={`pop-pill font-bold text-[10px] ${e.waste_pct > 5 ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300" : e.waste_pct > 2 ? "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"}`}>
                    {e.waste_pct.toFixed(2)} %
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <button
                    onClick={() => openPdf(`/production/${e.id}/completion.pdf`)}
                    data-testid={`btn-pdf-${e.charge_number}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--pop-pink)] hover:underline"
                    title="Abschluss-Protokoll anzeigen">
                    <FileDown size={13} /> PDF
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

const StatCard = ({ label, value, accent = "text-[var(--text)]" }) => (
  <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4">
    <div className="overline text-[var(--text-muted)]">{label}</div>
    <div className={`font-display text-3xl font-black mt-1 ${accent}`}>{value}</div>
  </div>
);
